import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { ReduxReusableComponents, RRCProps } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import _ from "lodash";
import React from "react";
import { Button, Checkbox, Dropdown, DropdownItemProps, Label, List } from "semantic-ui-react";
import { AbstractGantt, AbstractGanttProps, AbstractGanttReducers, AbstractGanttState, GanttData, GanttGroup } from "./AbstractGantt";
import { getLayerStyle } from "./ganttLayerStyles";
import { humanResourceScheduleEntityDescriptor, missionEntityDescriptor } from "AppEntityDescriptors";
import { GanttUtils } from "./GanttUtils";
import { IGanttAction, IGanttActionParamForRun } from "@crispico/react-timeline-10000";
import { equipmentResourceEntityDescriptor, EquipmentResourceTablePage } from "pages/EquipmentResource/equipmentResourceEntityDescriptor";
import { ColumnDefinition, EntityDescriptor, Utils } from "@crispico/foundation-react";
import { MissionTotalTimeFieldDescriptor } from "pages/CommonFieldDescriptors";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { PopupWithHelpTooltip } from "@crispico/foundation-react/components/semanticUiReactExt";

const NONE_ENTITY_NAME = "None";
const NONE_ID = 0;
const NONE_ENTITY_UID = GanttUtils.toEntityUid(NONE_ENTITY_NAME, NONE_ID);

export enum Sorting {
    MISSIONS_TOTAL_TIME = "MISSIONS_TOTAL_TIME",
    SHEDULE_START_AND_END_TIME = "SHEDULE_START_AND_END_TIME",
    EQUIPMENT_TYPE = "EQUIPMENT_TYPE",
}

export interface GanttResourcesOptions {
    sortBy: Sorting,
    groupBy: "HumanResourceSchedule" | "EquipmentResource",
    hideGroupBy: boolean,
    tableFields?: string[],
    showMessageButton: boolean
}

class GanttResourcesState extends AbstractGanttState {
    selectedGroupBy: "HumanResourceSchedule" | "EquipmentResource" | typeof NONE_ENTITY_NAME = NONE_ENTITY_NAME;
    selectedSortBy: Sorting = Sorting.MISSIONS_TOTAL_TIME;
    showMessageButton: boolean = true;
    showOnlyResourcesWithMissions: boolean = false;
    showBusiestSlotsPerEquipmentType: boolean = false;
    columnToScroll: number | undefined = undefined;
}
class GanttResourcesReducers<S extends GanttResourcesState = GanttResourcesState> extends AbstractGanttReducers<S> { }

type GanttResourcesProps = AbstractGanttProps & { hideGroupBy?: boolean, hideSortBy?: boolean };
type Props = RRCProps<GanttResourcesState, GanttResourcesReducers> & GanttResourcesProps;

export class GanttResources extends AbstractGantt<GanttResourcesProps, GanttResourcesReducers, GanttResourcesState> {

    constructor(props: Props) {
        super(props);
    }

    public getResourcesWithTheLeastTimeUsed(endNumber: number): number[] {
        return this.props.s.data.groups.filter((g: GanttGroup) => g.visible && g.entityId).slice(endNumber).map(g => g.entityId);
    }

    private getPendingMissions(entities: { [entityName: string]: { [id: number]: any } }) {
        let missions = entities?.["Mission2"] ? Object.keys(entities?.["Mission2"]).map(key => GanttUtils.findByUid(GanttUtils.toEntityUid("Mission2", Number(key)), entities)) : [];

        if (this.props.s.selectedGroupBy != NONE_ENTITY_NAME) {
            const field = this.props.s.selectedGroupBy == "EquipmentResource" ? "equipmentResource" : "humanResource";
            missions = missions.filter(m => !m[field]);
        }
        return missions;
    }

    private getMissionsTotalTime(entity: any) {
        if (!entity || !entity.missions) {
            return 0;
        }
        return entity.missions.reduce((missionsTotalTime: number, mission: any) => {
            return (mission.endTime - mission.startTime) + missionsTotalTime;
        }, 0)
    }

    protected sortData(data: { [key: number]: any }, type: Sorting, entities: { [entityName: string]: { [id: number]: any } }): any[] {
        if (this.props.s.selectedGroupBy === "EquipmentResource") {
            return Object.keys(data).map(key => GanttUtils.findByUid(GanttUtils.toEntityUid("EquipmentResource", Number(key)), entities)).sort((a, b) => {
                if (type === Sorting.MISSIONS_TOTAL_TIME) {
                    if (!a.identifier) {
                        return 1;
                    } else if (!b.identifier) {
                        return -1;
                    }
                    const diff = this.getMissionsTotalTime(b) - this.getMissionsTotalTime(a);
                    if (diff !== 0) {
                        return diff;
                    }
                }
                let valA;
                let valB;
                if (type === Sorting.EQUIPMENT_TYPE) {
                    valA = a.equipmentType?.name?.toLocaleLowerCase();
                    valB = b.equipmentType?.name?.toLocaleLowerCase();
                } else {
                    // default sort by identifier
                    valA = a.identifier?.toLocaleLowerCase();
                    valB = b.identifier?.toLocaleLowerCase();
                }
                return Utils.isNullOrEmpty(valA) ? 1 : (Utils.isNullOrEmpty(valB) ? -1 : valA.localeCompare(valB));
            });
        }
        return Object.keys(data).map(key => GanttUtils.findByUid(GanttUtils.toEntityUid("HumanResourceSchedule", Number(key)), entities)).sort((a, b) => {
            if (type === Sorting.MISSIONS_TOTAL_TIME && this.props.s.selectedGroupBy === "HumanResourceSchedule") {
                const missionsTotalTimeA = this.getMissionsTotalTime(a), missionsTotalTimeB = this.getMissionsTotalTime(b);
                if (missionsTotalTimeA == missionsTotalTimeB) {
                    return a.startTime - b.startTime;
                }
                return missionsTotalTimeB - missionsTotalTimeA;
            } else if (type === Sorting.SHEDULE_START_AND_END_TIME && this.props.s.selectedGroupBy === "HumanResourceSchedule") {
                if (b.startTime == a.startTime) {
                    return this.getMissionsTotalTime(b) - this.getMissionsTotalTime(a);
                }
                return a.startTime - b.startTime;
            } else if (type === Sorting.EQUIPMENT_TYPE && this.props.s.selectedGroupBy === "HumanResourceSchedule") {
                const valA = a.humanResource?.vehicle?.equipmentType?.name?.toLocaleLowerCase();
                const valB = b.humanResource?.vehicle?.equipmentType?.name?.toLocaleLowerCase();
                return Utils.isNullOrEmpty(valA) ? 1 : (Utils.isNullOrEmpty(valB) ? -1 : valA.localeCompare(valB));
            }
            return 0;
        });
    }

    protected processData(newEntities: any) {
        const perType = this.props.s.selectedGroupBy;
        const groupData: { [key: number]: any } = perType ? (newEntities?.[perType] || {}) : {};
        const data: GanttData = { layers: [], items: [], groups: [] };
        const sortedLines = this.sortData(groupData, this.props.s.selectedSortBy as Sorting, newEntities);
        var indexGroup = -1;

        sortedLines.push({ id: NONE_ID });

        sortedLines.forEach(r => {
            let addToGantt = true;
            if (perType && this.props.hideResources?.[perType] && this.props.hideResources[perType]?.findIndex(id => id === r.id) !== -1) {
                addToGantt = false;
            }
            indexGroup++;
            const isNoneRow = r.id == NONE_ID;
            let group: GanttGroup = {
                id: indexGroup,
                entityUid: isNoneRow ? NONE_ENTITY_UID : GanttUtils.toEntityUid(perType, r.id),
                key: isNoneRow ? NONE_ENTITY_UID : GanttUtils.toEntityUid(perType, r.id),
                visible: addToGantt,
                ganttId: this.constructor.name,
                equipmentTypeId: perType == "EquipmentResource" ? r.equipmentType?.id : (perType == "HumanResourceSchedule" ? r.humanResource?.equipmentResource?.equipmentType?.id : undefined),
            }
            data.groups.push(group);

            const resourceMissions = perType && (isNoneRow ? this.getPendingMissions(newEntities) : (GanttUtils.findByUid(GanttUtils.toEntityUid(perType!, r.id), newEntities)?.missions || [])) || [];
            resourceMissions.forEach((mission: any) => {
                data.items.push({
                    key: GanttUtils.toEntityUid(missionEntityDescriptor.name, mission.id),
                    row: indexGroup,
                    start: mission.startTime,
                    end: mission.endTime,
                    entityUid: GanttUtils.toEntityUid(missionEntityDescriptor.name, mission.id),//GanttUtils.denormalizeEntity("Mission2", mission, newEntities),
                    ganttId: this.constructor.name,
                    visible: addToGantt,
                });
            })

            if (this.props.s.selectedGroupBy == "HumanResourceSchedule" && !isNoneRow) {
                data.layers.push({
                    rowNumber: indexGroup,
                    start: r.startTime,
                    end: r.endTime,
                    style: getLayerStyle("HumanResourceSchedule", GanttUtils.findByUid(GanttUtils.toEntityUid("HumanResourceSchedule", r.id), newEntities)),
                    visible: addToGantt
                });
            }

            // if no missions exists for the group then don't display the resource and the layer
            if (this.props.s.showOnlyResourcesWithMissions && resourceMissions.length == 0) {
                data.groups.pop();
                data.layers.pop();
                indexGroup--;
            }
        });
        if (this.props.s.showBusiestSlotsPerEquipmentType) {
            this.addBusiestSlotPerEquipmentTypeLayers(data);
        }
        this.props.r.setInReduxState({ data });
    }

    /**
     * @deprecated
     * @protected This is old implementation, new implementation is in {@link EquipmentResourceTablePage#getBusiestSlotPerEquipmentTypeLayers}
     */
    protected addBusiestSlotPerEquipmentTypeLayers(data: GanttData) {
        if (data.items.length == 0) {
            return;
        }
        let resourcesGroupedByEquipmentType: { [key: string]: GanttGroup[] } = { "": data.groups };
        if (this.props.s.selectedSortBy === Sorting.EQUIPMENT_TYPE) {
            resourcesGroupedByEquipmentType = _.groupBy(data.groups, "equipmentTypeId");
        }
        Object.keys(resourcesGroupedByEquipmentType).forEach(equipmentTypeId => {
            const groups = resourcesGroupedByEquipmentType[equipmentTypeId];
            const items = data.items.filter(item => groups.find(group => group.id == item.row));
            const minStart = _.minBy(items, 'start')?.start as number;
            const maxStart = _.maxBy(items, 'end')?.end as number;
            if (!minStart || !maxStart) {
                return;
            }
            const intervalDuration = 30 * 60 * 1000; // 30 minute in ms
            const intervalsAndResourcesSize: [number, number][] = [];
            for (let startInterval = minStart; startInterval < maxStart; startInterval += intervalDuration) {
                const endInterval = startInterval + intervalDuration;
                const resources = new Set<number>();
                items.forEach(item => {
                    const startItem = item.start as number;
                    const endItem = item.end as number;
                    if ((startItem > startInterval && startItem < endInterval) || (endItem > startInterval && endItem < endInterval)) {
                        resources.add(item.row);
                    }
                });
                intervalsAndResourcesSize.push([startInterval, resources.size]);
            }
            const mostBusiestInterval = _.maxBy(intervalsAndResourcesSize, ([interval, value]) => value);
            const index = intervalsAndResourcesSize.indexOf(mostBusiestInterval!);
            // in order to show more segments, if the right neighbor interval has more resources than the left one then display the [T - 15min, T + 45min]
            // otherwise display the standard [T - 30 min, T + 30 min]
            const useRightNeighbor = intervalsAndResourcesSize[index + 1]?.[1] || -1 > intervalsAndResourcesSize[index - 1]?.[1] || -1;
            const start = mostBusiestInterval![0] - (useRightNeighbor ? 15 * 60 * 1000 : intervalDuration);
            const end = mostBusiestInterval![0] + (useRightNeighbor ? (15 * 60 * 1000) + intervalDuration : intervalDuration);
            groups.forEach(group => {
                data.layers.push({
                    rowNumber: group.id,
                    start: start,
                    end: end,
                    style: getLayerStyle("BusiestSlot", null),
                    visible: true
                });
            });
        })

    }

    async componentDidMount() {
        let selectedGroupBy = window.sessionStorage.getItem(GANTT_RESOURCES_GROUP_BY) as "HumanResourceSchedule" | "EquipmentResource" | typeof NONE_ENTITY_NAME;
        let selectedSortBy = window.sessionStorage.getItem(GANTT_RESOURCES_SORT_BY) as Sorting;
        if (selectedGroupBy === null) {
            selectedGroupBy = "HumanResourceSchedule";
        }
        if (selectedSortBy === null) {
            selectedSortBy = Sorting.MISSIONS_TOTAL_TIME;
        }
        await this.props.r.setInReduxState({ selectedGroupBy: selectedGroupBy, selectedSortBy: selectedSortBy });

        super.componentDidMount();
    }

    async componentDidUpdateInternal(prevProps?: Props) {
        super.componentDidUpdateInternal(prevProps);
        let shouldProcessData: boolean = false;
        if (prevProps && prevProps.s.selectedSortBy !== this.props.s.selectedSortBy) {
            window.sessionStorage.setItem(GANTT_RESOURCES_SORT_BY, this.props.s.selectedSortBy as Sorting);
            shouldProcessData = true;
        }
        if (!prevProps || prevProps.s.selectedGroupBy !== this.props.s.selectedGroupBy) {
            window.sessionStorage.setItem(GANTT_RESOURCES_GROUP_BY, this.props.s.selectedGroupBy as string);
            shouldProcessData = true;
        }
        if (!prevProps || prevProps.s.showOnlyResourcesWithMissions !== this.props.s.showOnlyResourcesWithMissions
            || prevProps.s.showBusiestSlotsPerEquipmentType !== this.props.s.showBusiestSlotsPerEquipmentType) {
            shouldProcessData = true;
        }
        if (shouldProcessData) {
            this.processData(this.props.entities);
        }
    }

    protected renderAdditionalContextMenuAction(entityUid: string): IGanttAction[] {
        const { id } = GanttUtils.fromEntityUid(entityUid);
        return [{
            icon: 'info circle',
            label: _msg("GanttResource.contextMenu.showDetails.label"),
            run: (param: IGanttActionParamForRun) => AppMetaTempGlobals.history.push(entityDescriptors["Mission2"].getEntityEditorUrl(id) + "/auditGraph")
        }];
    }

    protected entitiesChangedHandler(newEntities: any) {
        this.processData(newEntities);
    }

    protected getAcceptedType() {
        return "Mission2";
    }
    protected getContextProps() {
        return {
            ...super.getContextProps(), showGanttMessageModal: (driverId: number) => {
                // if popup is open, close it
                this.props.s.popupEntityUid && this.props.r.setInReduxState({ popupEntityUid: undefined });
            }
        };
    }
    protected getEntityAt(index: number) {
        const group = this.props.s.data.groups[index];
        return group?.entityUid == NONE_ENTITY_UID ? { id: NONE_ID, missions: this.getPendingMissions(this.props.entities!) } : super.getEntityAt(index);
    }

    protected getEntityDescriptor() {
        if (this.props.s.selectedGroupBy === "HumanResourceSchedule") {
            return humanResourceScheduleEntityDescriptor;

        } else if (this.props.s.selectedGroupBy === "EquipmentResource") {
            return equipmentResourceEntityDescriptor;
        }
        return noneEntityDescriptor;
    }

    protected getColumnToScroll() {
        return this.props.s.columnToScroll;
    }

    protected getTableColumns(): ColumnDefinition[] | undefined {
        let columns = super.getTableColumns();
        if (columns) {
            return columns.concat([{ name: "missionsTotalTime", width: 120 }]);
        }
        if (this.props.s.selectedGroupBy === "HumanResourceSchedule") {
            const fields = ["humanResource", "humanResource.vehicle", "startTime", "endTime", "regulator", "mole", "teamName", "missionsTotalTime"];
            let cc: ColumnDefinition[] = [];
            fields.forEach(f => cc.push({ width: 120, name: f }));
            return cc;
        } else if (this.props.s.selectedGroupBy === "EquipmentResource") {
            return [{ name: "identifier", width: this.getTableWidth() * 2 / 3 }, { name: "missionsTotalTime", width: this.getTableWidth() / 3 }];
        }
        return [{ name: "missionsTotalTime", width: this.getTableWidth() }];
    }

    protected renderTopBar() {
        const optionsGroup: DropdownItemProps[] = [{ value: "HumanResourceSchedule", text: entityDescriptors["HumanResourceSchedule"].getLabel() },
        { value: "EquipmentResource", text: entityDescriptors["EquipmentResource"].getLabel() },
        { value: NONE_ENTITY_NAME, text: _msg("GanttResources.none") }];
        const optionsSort: DropdownItemProps[] = [{ value: Sorting.MISSIONS_TOTAL_TIME, text: _msg("GanttResources.sortBy.missionTime") },
        { value: Sorting.SHEDULE_START_AND_END_TIME, text: _msg("GanttResources.sortBy.sheduleTime") },
        { value: Sorting.EQUIPMENT_TYPE, text: _msg("GanttResources.sortBy.equipmentType") }];       
        const resourcesCountByEquipmentType = _.countBy(this.props.s.data.groups, "equipmentTypeId");
        return <>{!this.props.hideSortBy ? <>
            <div className="flex-container">
                {_msg("GanttResources.sortBy")}
                <Dropdown selection options={optionsSort} value={this.props.s.selectedSortBy} onChange={(e, data) =>
                    this.props.r.setInReduxState({ selectedSortBy: data.value as Sorting })} />
            </div>
            <div className="flex-container">{_msg("GanttResources.onlyResourcesWithMissions.title")}
                <Checkbox checked={this.props.s.showOnlyResourcesWithMissions} onClick={(event, data) =>
                    this.props.r.setInReduxState({ showOnlyResourcesWithMissions: data.checked ? true : false })
                } />
            </div>
            <div className="flex-container">{_msg("GanttResources.showBusiestSlotsPerEquipmentType.title")}
                <Checkbox checked={this.props.s.showBusiestSlotsPerEquipmentType} onClick={(_, data) =>
                    this.props.r.setInReduxState({ showBusiestSlotsPerEquipmentType: data.checked ? true : false })
                } />
            </div>
            <div className="flex-container">
                <PopupWithHelpTooltip popupProps={{ position: "bottom left" }} buttonProps={{ icon: "info" }} content={
                    <div data-testid="resourcesCountByEquipmentType">
                        <span>{entityDescriptors["EquipmentType"].getLabel(true)}</span>
                        <List className="GanttResources_countEquipmentResourceTypesTooltip">
                            {Object.entries(resourcesCountByEquipmentType).sort((a, b) => b[1] - a[1]).map(entry => {
                                const equipmentType = ["", "undefined", "null"].includes(entry[0]) ? { name: _msg("GanttResources.none") }
                                    : { ...this.props.entities?.["EquipmentType"][Number(entry[0])] };
                                return <>
                                    <List.Item key={entry[0]}>
                                        <div className="flex-container-row">
                                            <Label circular content={entry[1]} style={{
                                                backgroundColor: Utils.convertColorToHex(equipmentType.color || 0),
                                                color: Utils.convertColorToHex(Utils.getContrastingForegroundColor(equipmentType.color || 0))
                                            }} />
                                            {entityDescriptors["EquipmentResource"].getField("equipmentType").renderField({ equipmentType: equipmentType })}
                                        </div>
                                    </List.Item>
                                </>
                            })}
                        </List>
                    </div>
                } />
            </div>            
        </> : <></>}{!this.props.hideGroupBy ? <>
            <div className="flex-container">
                {_msg("GanttResources.groupBy")}
                <Dropdown selection options={optionsGroup} value={this.props.s.selectedGroupBy} onChange={(e, data) => {
                    this.props.r.setInReduxState({ selectedGroupBy: data.value as "HumanResourceSchedule" | "EquipmentResource" | typeof NONE_ENTITY_NAME });
                }
                } />
            </div>
        </> : <></>}</>;
    }
}

export const GanttResourcesRRC = ReduxReusableComponents.connectRRC(GanttResourcesState, GanttResourcesReducers, GanttResources);

const GANTT_RESOURCES_SORT_BY = 'ganttResources.sortBy';
const GANTT_RESOURCES_GROUP_BY = 'ganttResources.groupBy';

export const noneEntityDescriptor = new EntityDescriptor({ name: "None" })
    .addFieldDescriptor(new MissionTotalTimeFieldDescriptor())
    .addFieldDescriptor({ name: "missions", type: FieldType.oneToMany("Mission2") });
